<template>
  <billing-dialog
    :visible="visible"
    class="opc-ze-capacity-dialog"
    :title="$t('billing.events.capacityInfo.title', [zeTypeName])"
    title-direction="center"
    :before-close='handleBeforeClose'
    @back-event="handleBeforeClose"
  >
    <template v-if="isSaas">
      <p class="opc-ze-capacity-dialog__saas-desc">
        {{ $t('billing.events.capacityInfo.saas.desc', [zeTypeName]) }}
      </p>
      <zm-table
        :data="tableData"
        style="width: 100%"
      >
        <zm-table-column>
          <template slot="header">
            {{ $t('billing.events.capacityInfo.xx_license_amount', [zeTypeName]) }}
          </template>
          <template slot-scope="{row}">
            {{ $t(row.title) }}
          </template>
        </zm-table-column>

        <zm-table-column
          v-for="(item, key) in configColumnMap"
          :key="key"
          :width="columnWidth"
        >
          <template slot="header">
            <div class="opc-ze-capacity-dialog__cell-header">
              <p class="opc-ze-capacity-dialog__cell-header-capacity">{{toThousands(key)}}</p>
              <p class="opc-ze-capacity-dialog__cell-header-desc">{{$t(item.subtitle)}}</p>
            </div>
          </template>
          <template slot-scope="{row}">
            <span class="opc-zecp__cell">
              <span class="opc-zecp__cell__left">
                <p>{{ $t('billing.events.capacityInfo.xx_license_amount', [zeTypeName]) }}</p>
                <p>{{toThousands(key)}} ({{$t(item.subtitle)}})</p>
              </span>
              <span class="opc-zecp__cell__right">
                {{ toThousands(item[row.key]) }}
              </span>
            </span>
          </template>
        </zm-table-column>
      </zm-table>
    </template>
    <template v-if="isConsumption">
      <p class="opc-ze-capacity-dialog__ppa-desc" v-html="$t('billing.events.capacityInfo.ppa.desc', [overagePrice])" />
    </template>
  </billing-dialog>
</template>
<script>
import { planTypes } from '@/common/constants';
import { mapState } from 'vuex';
import { get } from 'lodash';
import { toThousands, transferMoney } from '@/utils/money';
import { getPlanTypeName } from '@/common/service/parse/parse-product-name';
import { ConfirmComponent } from '@/components/common/service-dialog/confirm';
import { EventModeMap, ze_only_overage_price_list_key } from '@/plans/EVENT_SAAS/helper/constant';
import { translation } from '@/plugins/i18n';
import store from '@/store';
import { plansof } from '@/utils/chain';
import {
  isOnlineNotSupportPlan,
} from '@/common/service/non-standard-billing-cycle-utils';
import BillingDialog from '@/components/billing-dialog.vue';

const eventSaasTableData = [{
  title: 'billing.events.capacityInfo.max_allowed_in_meeting',
  key: 'meeting',
}, {
  title: 'billing.events.capacityInfo.max_allowed_in_webinar',
  key: 'webinar',
}];

const eventSaasFeatures = {
  100: {
    meeting: 100,
    webinar: 100,
    get subtitle() {
      const annual = translation('billing.cycle.annual');
      const monthly = translation('billing.billing_cycle.monthly');
      const or = translation('billing.common.or');

      return `${monthly} ${or} ${annual}`;
    },
  },
  500: {
    meeting: 500,
    webinar: 500,
    subtitle: 'billing.cycle.annual',
  },
  1000: {
    meeting: 1000,
    webinar: 1000,
    subtitle: 'billing.cycle.annual',
  },
  3000: {
    meeting: 1000,
    webinar: 3000,
    subtitle: 'billing.cycle.annual',
  },
  // 5000: {
  //   meeting: 1000,
  //   webinar: 5000,
  //   subtitle: 'billing.cycle.annual',
  // },
  // 10000: {
  //   meeting: 1000,
  //   webinar: 10000,
  //   subtitle: 'billing.cycle.annual',
  // },
}

const eventSaasCoreTableData = [{
  title: 'billing.events.capacityInfo.max_allowed_in_meeting',
  key: 'meeting',
}, {
  title: 'billing.events.capacityInfo.max_allowed_in_webinar',
  key: 'webinar',
}];

const formatOverageChargeAmount = (excessPrice) => {
  if (typeof excessPrice === "number") {
    return transferMoney(excessPrice);
  }

  return excessPrice;
}

export default {
  components: { BillingDialog },
  mixins: [ConfirmComponent],
  data() {
    return {
      visible: false,
      zeType: '',
      zeMode: '',
    };
  },
  computed: {
    ...mapState({
      allPlanMap: state => state.ShoppingCart.allPlanMap,
    }),
    isSaas() {
      return this.zeMode === EventModeMap.SUBSCRIPTION;
    },
    isConsumption() {
      return this.zeMode === EventModeMap.PAY_PER_ATTENDEE;
    },
    zeOverage() {
      return get(this.allPlanMap, ze_only_overage_price_list_key, {});
    },
    zeTypeName() {
      return this.$t(getPlanTypeName(this.zeType));
    },
    overagePrice() {
      const { zeco_overage, zecco_overage } = this.zeOverage;

      switch (this.zeType) {
        case planTypes.EVENT_SAAS:
          return formatOverageChargeAmount(zeco_overage?.excessPriceCents || zeco_overage?.excessPrice);
        case planTypes.EVENT_SAAS_CORE:
          return formatOverageChargeAmount(zecco_overage?.excessPriceCents || zecco_overage?.excessPrice);
        default:
          return '';
      }
    },
    tableData() {
      switch (this.zeType) {
        case planTypes.EVENT_SAAS:
          return eventSaasTableData;
        case planTypes.EVENT_SAAS_CORE:
          return eventSaasCoreTableData;
        default:
          return [];
      }
    },
    configColumnMap() {
      const plan = plansof(store.getters.getSelectedPlans).type(this.$context?.type).get(0);
      const billingCycle = plan?.billing_cycle;
      if (isOnlineNotSupportPlan({ billingCycle })) {
        Object.keys(eventSaasFeatures).forEach(k => {
          Object.defineProperty(eventSaasFeatures[k], 'subtitle', {
            get: () => '',
          });
        });
      }
      return eventSaasFeatures;
    },
    columnWidth() {
      const featureCount = Object.keys(this.configColumnMap).length;
      return (900 - 170) / featureCount;
    },
  },
  watch: {
    visible: {
      immediate: true,
      handler(v) {
        v && this.doTracking()
      }
    }
  },
  methods: {
    doTracking() {
      let trackingPlanName = ''
      if (this.zeType === planTypes.EVENT_SAAS) {
        trackingPlanName = "zoom events"
      } else if (this.zeType === planTypes.EVENT_SAAS_CORE) {
        trackingPlanName = "zoom sessions"
      }
      this.$tracking.buyInteraction(`${trackingPlanName} plan`,
        `attendee capacity in ${trackingPlanName} modal - impression`);
    },
    handleBeforeClose() {
      this.$tracking.buyInteraction(`${this.trackingPlanKey} plan`,
        'attendee capacity in zoom events modal - exit');
      this.handleSure();
    },
    toThousands() {
      return toThousands(...arguments);
    },
  },
};
</script>
<style lang="scss">
@import '@/styles/_variables.scss';

.opc-ze-capacity-dialog {
  &__saas-desc, &__ppa-desc {
    color: #00053D;
    font-family: Almaden Sans;
    font-size: 16px;
    font-style: normal;
    font-weight: 450;
    line-height: 150%; /* 24px */
    margin-bottom: 16px;
  }

  &__cell-header {
    display: flex;
    justify-content: center;
  }

  &__cell-header-capacity {
    color: #00053D;
    text-align: center;
    font-family: Almaden Sans;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 120%; /* 16.8px */
  }

  &__cell-header-desc {
    color: #666487;
    text-align: center;
    font-family: Almaden Sans;
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: 120%; /* 14.4px */
  }

  .zm-dialog {
    max-width: 1000px;
    width: auto;
    padding: 32px;

    &__title {
      color: #222230;
      font-family: $happy-display;
      font-size: 24px;
      font-style: normal;
      font-weight: 600;
      line-height: 120%; /* 28.8px */
    }

    .zm-dialog__header {
      padding-bottom: 24px;
      font-family: 'Inter';
      font-style: normal;
      font-weight: 700;
      font-size: 24px;
      line-height: 29px;
      color: #222230;
    }
  }

  .zm-table {
    font-family: 'Inter';
    font-style: normal;
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    color: #000000;

    thead {
      th {
        font-family: 'Inter';
        font-style: normal;
        font-weight: 700;
        font-size: 14px;
        line-height: 20px;
        color: #000000;
        background: #E1E1E6;

        .cell {
          justify-content: center;
        }
      }
    }

    th, td {
      border: 1px solid #F2F2F4;

      .cell {
        text-align: center;
      }
    }

    th:first-child, th {
      .cell {
        color: #00053D;
        font-family: Almaden Sans;
        font-size: 14px;
        font-style: normal;
        font-weight: 500;
        line-height: 120%;
      }
    }

    tr td {
      .cell {
        color: #00053D;
        font-family: Almaden Sans;
        font-size: 14px;
        font-style: normal;
        font-weight: 500;
        line-height: 120%;
      }
    }
  }
}

.opc-zecp__cell {
  color: #666487;
  text-align: center;
  font-family: Almaden Sans;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 120%; /* 14.4px */
  &__left{
    display: none;
  }
  &__right {
    color: #00053d;
    text-align: center;
    font-family: Almaden Sans;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 120%; /* 16.8px */
  }
}
.zm-table__row.is-flip {
  &:hover td{
    background: none !important;
  }
  td:first-child{
    background: #F2F2F7 !important;
  }
  .opc-zecp__cell__left{
    display: block;
  }
}
</style>
